import { useState, useEffect } from 'react';
import { X, FileText, Variable, BarChart3, Send, CheckCircle, Eye, MousePointerClick, DollarSign, TrendingUp, ArrowUpRight, ArrowDownRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import WhatsAppPreview from './WhatsAppPreview';

interface WabaTemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    id: string;
    name: string;
    status: string;
    category: string;
    language: string;
    components: any[];
    createdAt: string;
    updatedAt: string;
    analytics?: {
      totals: {
        sent: number;
        delivered: number;
        read: number;
        clicked: Array<{ type: string; total: number; unique: number }>;
        cost: Array<{ amount: number; currency: string }>;
      };
      daily: Array<{
        date: string;
        sent: number;
        delivered: number;
        read: number;
      }>;
      lastFetchedAt: string;
      dataRange: { start: string; end: string };
    } | null;
  };
}

export function WabaTemplatePreviewModal({ isOpen, onClose, template }: WabaTemplatePreviewModalProps) {
  const hasAnalyticsData = template.analytics && template.analytics.totals.sent > 0;
  const [rightTab, setRightTab] = useState<'info' | 'analytics'>(hasAnalyticsData ? 'analytics' : 'info');
  const [showStructure, setShowStructure] = useState(false);

  useEffect(() => {
    setRightTab(hasAnalyticsData ? 'analytics' : 'info');
  }, [template.id]);

  if (!isOpen) return null;

  const headerComponent = template.components?.find((c: any) => c.type === 'HEADER');
  const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
  const footerComponent = template.components?.find((c: any) => c.type === 'FOOTER');
  const buttonsComponent = template.components?.find((c: any) => c.type === 'BUTTONS');

  const extractVariables = (text: string): string[] => {
    if (!text) return [];
    return text.match(/\{\{(\d+)\}\}/g) || [];
  };
  const bodyVariables = bodyComponent?.text ? extractVariables(bodyComponent.text) : [];
  const headerVariables = headerComponent?.text ? extractVariables(headerComponent.text) : [];
  const totalVariables = [...headerVariables, ...bodyVariables];

  const getBodyExamples = () => bodyComponent?.example?.body_text?.[0] || [];
  const getHeaderExample = () => headerComponent?.example?.header_text?.[0] || '';
  const getHeaderMediaUrl = () => headerComponent?.example?.header_handle?.[0] || '';

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = { UTILITY: 'Utilidade', MARKETING: 'Marketing', AUTHENTICATION: 'Autenticação' };
    return labels[category] || category;
  };

  const getStatusColor = (status: string) => {
    if (status === 'APPROVED' || status.startsWith('ACTIVE')) return 'bg-success/10 text-success';
    if (status === 'REJECTED' || status === 'DISABLED') return 'bg-danger/10 text-danger';
    return 'bg-warning/10 text-warning';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      PENDING: 'Pendente', APPROVED: 'Aprovado', REJECTED: 'Rejeitado',
      ACTIVE_QUALITY_PENDING: 'Ativo - Qualidade Pendente', ACTIVE_HIGH_QUALITY: 'Ativo - Alta Qualidade',
      ACTIVE_MEDIUM_QUALITY: 'Ativo - Média Qualidade', ACTIVE_LOW_QUALITY: 'Ativo - Baixa Qualidade',
      PAUSED: 'Pausado', DISABLED: 'Desabilitado',
    };
    return labels[status] || status;
  };

  const hasAnalytics = template.analytics && template.analytics.totals.sent > 0;

  // ── Componente do painel de informações ──
  const InfoPanel = () => (
    <div className="space-y-4">
      {/* Variáveis */}
      {totalVariables.length > 0 && (
        <div className="bg-accent-2/5 border border-accent-2/20 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Variable className="text-accent-2" size={14} />
            <span className="text-xs font-bold text-text-1">Variáveis</span>
            <span className="ml-auto px-1.5 py-0.5 bg-accent-2/10 text-accent-2 rounded text-[10px] font-semibold">
              {totalVariables.length}
            </span>
          </div>
          <div className="space-y-1">
            {headerVariables.map((variable, index) => (
              <div key={`h-${index}`} className="flex items-center gap-2 text-[11px]">
                <span className="w-5 h-5 flex items-center justify-center bg-accent-2/10 text-accent-2 rounded text-[10px] font-bold">{index + 1}</span>
                <span className="font-mono font-bold text-accent-2">{variable}</span>
                <span className="ml-auto px-1.5 py-0.5 bg-info/10 text-accent-1 rounded text-[10px]">Cabeçalho</span>
              </div>
            ))}
            {bodyVariables.map((variable, index) => (
              <div key={`b-${index}`} className="flex items-center gap-2 text-[11px]">
                <span className="w-5 h-5 flex items-center justify-center bg-accent-2/10 text-accent-2 rounded text-[10px] font-bold">{headerVariables.length + index + 1}</span>
                <span className="font-mono font-bold text-accent-2">{variable}</span>
                <span className="ml-auto px-1.5 py-0.5 bg-success/10 text-success rounded text-[10px]">Corpo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estrutura Técnica — colapsável */}
      <div className="bg-bg-2 border border-border-1 rounded-lg">
        <button
          onClick={() => setShowStructure(!showStructure)}
          className="w-full flex items-center gap-1.5 p-3 hover:bg-bg-3/50 transition-colors rounded-lg"
        >
          <FileText className="text-text-2" size={14} />
          <span className="text-xs font-bold text-text-1">Estrutura Técnica</span>
          <span className="ml-auto text-text-3">
            {showStructure ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>
        {showStructure && (
          <div className="px-3 pb-3 space-y-1.5">
            {template.components?.map((component: any, index: number) => (
              <div key={index} className="bg-bg-1 border border-border-1 rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-bold text-text-1 px-2 py-0.5 bg-bg-3 rounded">{component.type}</span>
                  {component.format && (
                    <span className="text-[10px] font-semibold text-text-3 bg-bg-3 px-1.5 py-0.5 rounded">{component.format}</span>
                  )}
                </div>
                {component.text && (
                  <p className="text-[11px] text-text-2 font-mono leading-relaxed mt-1 line-clamp-3">{component.text}</p>
                )}
                {component.buttons && (
                  <div className="mt-1.5 space-y-1">
                    {component.buttons.map((button: any, btnIndex: number) => (
                      <div key={btnIndex} className="text-[11px] bg-info/5 p-1.5 rounded flex items-center justify-between">
                        <span className="font-medium text-text-1">{button.text}</span>
                        <span className="px-1.5 py-0.5 bg-info/10 text-accent-1 rounded text-[10px] font-semibold">{button.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Datas */}
      <div className="flex gap-3 text-[11px]">
        <div className="flex-1 bg-bg-2 border border-border-1 rounded-lg p-2.5">
          <p className="text-text-3 font-medium mb-0.5">Criado em</p>
          <p className="font-semibold text-text-1">{new Date(template.createdAt).toLocaleString('pt-BR')}</p>
        </div>
        <div className="flex-1 bg-bg-2 border border-border-1 rounded-lg p-2.5">
          <p className="text-text-3 font-medium mb-0.5">Atualizado em</p>
          <p className="font-semibold text-text-1">{new Date(template.updatedAt).toLocaleString('pt-BR')}</p>
        </div>
      </div>
    </div>
  );

  // ── Componente do painel de analytics ──
  const AnalyticsPanel = () => {
    if (!hasAnalytics) return null;
    const { totals, daily, lastFetchedAt, dataRange } = template.analytics!;
    const deliveryRate = totals.sent > 0 ? (totals.delivered / totals.sent) * 100 : 0;
    const readRate = totals.delivered > 0 ? (totals.read / totals.delivered) * 100 : 0;
    const totalClicks = totals.clicked?.reduce((sum, c) => sum + (c.total || 0), 0) || 0;
    const uniqueClicks = totals.clicked?.reduce((sum, c) => sum + (c.unique || 0), 0) || 0;
    const maxDailySent = daily.length > 0 ? Math.max(...daily.map(d => d.sent), 1) : 1;
    const last7 = daily.slice(-7);
    const prev7 = daily.slice(-14, -7);
    const last7Sent = last7.reduce((s, d) => s + d.sent, 0);
    const prev7Sent = prev7.reduce((s, d) => s + d.sent, 0);
    const trendPercent = prev7Sent > 0 ? ((last7Sent - prev7Sent) / prev7Sent) * 100 : 0;
    const trendUp = trendPercent >= 0;

    return (
      <div className="space-y-4">
        {/* Período e última atualização */}
        <div className="flex items-center justify-between text-[10px] text-text-3">
          <span>{dataRange.start} — {dataRange.end}</span>
          <span>Atualizado em {new Date(lastFetchedAt).toLocaleString('pt-BR')}</span>
        </div>

        {/* Cards de métricas */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Send size={14} className="text-accent-1" />
                <span className="text-xs text-text-3 font-medium">Enviadas</span>
              </div>
              {daily.length >= 14 && (
                <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${trendUp ? 'text-success' : 'text-danger'}`}>
                  {trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {Math.abs(trendPercent).toFixed(0)}%
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-text-1">{totals.sent.toLocaleString('pt-BR')}</p>
            <p className="text-[10px] text-text-3 mt-1">Total nos últimos 30 dias</p>
          </div>

          <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
            <div className="flex items-center gap-1.5 mb-2">
              <CheckCircle size={14} className="text-success" />
              <span className="text-xs text-text-3 font-medium">Entregues</span>
            </div>
            <p className="text-2xl font-bold text-success">{totals.delivered.toLocaleString('pt-BR')}</p>
            <div className="mt-1.5">
              <span className="text-[10px] text-text-3">{deliveryRate.toFixed(1)}% das enviadas</span>
              <div className="w-full h-1.5 bg-bg-3 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-success rounded-full transition-all" style={{ width: `${Math.min(deliveryRate, 100)}%` }} />
              </div>
            </div>
          </div>

          <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
            <div className="flex items-center gap-1.5 mb-2">
              <Eye size={14} className="text-accent-2" />
              <span className="text-xs text-text-3 font-medium">Lidas</span>
            </div>
            <p className="text-2xl font-bold text-accent-2">{totals.read.toLocaleString('pt-BR')}</p>
            <div className="mt-1.5">
              <span className="text-[10px] text-text-3">{readRate.toFixed(1)}% das entregues</span>
              <div className="w-full h-1.5 bg-bg-3 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-accent-2 rounded-full transition-all" style={{ width: `${Math.min(readRate, 100)}%` }} />
              </div>
            </div>
          </div>

          <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
            <div className="flex items-center gap-1.5 mb-2">
              <MousePointerClick size={14} className="text-warning" />
              <span className="text-xs text-text-3 font-medium">Cliques</span>
            </div>
            <p className="text-2xl font-bold text-warning">{totalClicks.toLocaleString('pt-BR')}</p>
            {uniqueClicks > 0 && (
              <p className="text-[10px] text-text-3 mt-1">{uniqueClicks.toLocaleString('pt-BR')} cliques únicos</p>
            )}
          </div>
        </div>

        {/* Custos */}
        {totals.cost?.length > 0 && totals.cost.some(c => c.amount > 0) && (
          <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <DollarSign size={14} className="text-text-2" />
                <span className="text-xs text-text-3 font-medium">Custo Total (últimos 30 dias)</span>
              </div>
              <span className="text-lg font-bold text-text-1">
                {totals.cost.map(c => `${c.currency} ${c.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join(' | ')}
              </span>
            </div>
          </div>
        )}

        {/* Gráfico de barras empilhadas */}
        {daily.length > 0 && (() => {
          // Calcular linhas de grade (4 níveis)
          const gridLines = [0.25, 0.5, 0.75, 1.0];
          const niceMax = (() => {
            const raw = maxDailySent;
            const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
            const normalized = raw / magnitude;
            if (normalized <= 1) return magnitude;
            if (normalized <= 2) return 2 * magnitude;
            if (normalized <= 5) return 5 * magnitude;
            return 10 * magnitude;
          })();

          return (
            <div className="bg-bg-2 rounded-xl p-4 border border-border-1">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-text-2" />
                  <span className="text-xs font-bold text-text-1">Envios Diários</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-text-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-sm bg-accent-1" />
                    Enviadas
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-sm bg-success" />
                    Entregues
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-sm bg-accent-2" />
                    Lidas
                  </div>
                </div>
              </div>

              {/* Área do gráfico com eixo Y */}
              <div className="flex gap-1">
                {/* Labels do eixo Y */}
                <div className="flex flex-col justify-between h-40 text-[9px] text-text-3 text-right pr-1 py-0" style={{ width: '32px', minWidth: '32px' }}>
                  {[...gridLines].reverse().map((pct) => (
                    <span key={pct}>{Math.round(niceMax * pct).toLocaleString('pt-BR')}</span>
                  ))}
                  <span>0</span>
                </div>

                {/* Gráfico */}
                <div className="flex-1 relative h-40">
                  {/* Linhas horizontais de grade */}
                  {gridLines.map((pct) => (
                    <div
                      key={pct}
                      className="absolute left-0 right-0 border-t border-border-1"
                      style={{ bottom: `${pct * 100}%` }}
                    />
                  ))}
                  {/* Linha base */}
                  <div className="absolute left-0 right-0 bottom-0 border-t border-border-1" />

                  {/* Barras sobrepostas (sent > delivered > read) */}
                  <div className="flex items-end gap-[2px] h-full relative z-10">
                    {daily.map((day, i) => {
                      const sentPct = (day.sent / niceMax) * 100;
                      const deliveredPct = (day.delivered / niceMax) * 100;
                      const readPct = (day.read / niceMax) * 100;

                      return (
                        <div key={i} className="flex-1 relative group cursor-default" style={{ height: '100%' }}>
                          {/* Enviadas (fundo — maior) */}
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-accent-1 rounded-t-sm transition-opacity group-hover:opacity-80"
                            style={{ height: `${Math.max(sentPct, 1)}%` }}
                          />
                          {/* Entregues (meio) */}
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-success rounded-t-sm transition-opacity group-hover:opacity-80"
                            style={{ height: `${Math.max(deliveredPct, 0)}%` }}
                          />
                          {/* Lidas (frente — menor) */}
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-accent-2 rounded-t-sm transition-opacity group-hover:opacity-80"
                            style={{ height: `${Math.max(readPct, 0)}%` }}
                          />

                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                            <div style={{ background: '#1e293b', color: '#fff' }} className="text-[10px] rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-xl">
                              <p className="font-semibold mb-0.5">{new Date(day.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>
                              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-sm bg-accent-1" /><span>{day.sent.toLocaleString('pt-BR')} enviadas</span></div>
                              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-sm bg-success" /><span>{day.delivered.toLocaleString('pt-BR')} entregues</span></div>
                              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-sm bg-accent-2" /><span>{day.read.toLocaleString('pt-BR')} lidas</span></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Eixo X */}
              <div className="flex justify-between mt-2" style={{ paddingLeft: '36px' }}>
                <span className="text-[10px] text-text-3">{new Date(dataRange.start + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span>
                <span className="text-[10px] text-text-3">{new Date(dataRange.end + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span>
              </div>
            </div>
          );
        })()}

        {/* Resumo de performance */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-bg-2 rounded-xl p-3 border border-border-1 text-center">
            <p className="text-[10px] text-text-3 font-medium mb-1">Taxa de Entrega</p>
            <p className="text-lg font-bold text-success">{deliveryRate.toFixed(1)}%</p>
          </div>
          <div className="bg-bg-2 rounded-xl p-3 border border-border-1 text-center">
            <p className="text-[10px] text-text-3 font-medium mb-1">Taxa de Leitura</p>
            <p className="text-lg font-bold text-accent-2">{readRate.toFixed(1)}%</p>
          </div>
          <div className="bg-bg-2 rounded-xl p-3 border border-border-1 text-center">
            <p className="text-[10px] text-text-3 font-medium mb-1">Média Diária</p>
            <p className="text-lg font-bold text-text-1">
              {daily.length > 0 ? Math.round(totals.sent / daily.length).toLocaleString('pt-BR') : '0'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="bg-bg-1 rounded-lg shadow-xl max-h-[92vh] flex w-full"
        style={{ maxWidth: hasAnalytics ? '1100px' : '880px' }}
      >
        {/* PAINEL ESQUERDO — Preview do WhatsApp */}
        <div className="flex flex-col w-[380px] min-w-[380px] border-r border-border-1">
          <div className="p-3 border-b border-border-1 bg-bg-2 rounded-tl-lg">
            <h3 className="text-xs font-medium text-text-3 uppercase tracking-wider">Preview da Mensagem</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            <WhatsAppPreview
              headerFormat={headerComponent?.format as any}
              headerText={headerComponent?.format === 'TEXT' ? headerComponent?.text : undefined}
              headerExample={getHeaderExample()}
              headerMediaUrl={getHeaderMediaUrl()}
              bodyText={bodyComponent?.text || ''}
              bodyExamples={getBodyExamples()}
              footerText={footerComponent?.text}
              buttons={buttonsComponent?.buttons || []}
              highlightVariables
            />
          </div>
        </div>

        {/* PAINEL DIREITO — Info + Analytics */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header com nome do template e tabs */}
          <div className="p-4 border-b border-border-1">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-text-1 truncate">{template.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-md ${getStatusColor(template.status)}`}>
                    {getStatusLabel(template.status)}
                  </span>
                  <span className="text-[11px] text-text-3 font-medium">{getCategoryLabel(template.category)}</span>
                  <span className="text-[11px] text-text-3">•</span>
                  <span className="text-[11px] text-text-3 uppercase">{template.language}</span>
                </div>
              </div>
              <button onClick={onClose} className="text-text-3 hover:text-text-1 transition-colors ml-2 mt-0.5">
                <X size={18} />
              </button>
            </div>

            {/* Tabs — só mostra se tem analytics */}
            {hasAnalytics && (
              <div className="flex gap-1 bg-bg-2 p-1 rounded-lg">
                <button
                  onClick={() => setRightTab('info')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    rightTab === 'info'
                      ? 'bg-bg-1 text-text-1 shadow-sm'
                      : 'text-text-3 hover:text-text-2'
                  }`}
                >
                  <Info size={13} />
                  Detalhes
                </button>
                <button
                  onClick={() => setRightTab('analytics')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    rightTab === 'analytics'
                      ? 'bg-bg-1 text-text-1 shadow-sm'
                      : 'text-text-3 hover:text-text-2'
                  }`}
                >
                  <BarChart3 size={13} />
                  Estatísticas
                </button>
              </div>
            )}
          </div>

          {/* Conteúdo baseado na tab */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
            {(!hasAnalytics || rightTab === 'info') && <InfoPanel />}
            {hasAnalytics && rightTab === 'analytics' && <AnalyticsPanel />}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border-1">
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 bg-bg-3 hover:bg-bg-3/80 text-text-1 rounded-md font-medium transition-colors text-sm"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
